import {
  EventType,
  ICreateRoomStateEvent,
  JoinRule,
  MatrixClient,
  RestrictedAllowType,
} from 'matrix-js-sdk/src/matrix';

import { buildCanonicalParentEvent, CustomMeedioMatrixEvents, getViaServers } from 'utils/matrixUtils';
import { PermissionId, workspacesConfig } from 'utils/powerLevelsConfig';

export const waitingListState = {
  type: EventType.RoomJoinRules,
  content: {
    join_rule: JoinRule.Knock,
  },
};

export const encryptionState = {
  type: EventType.RoomEncryption,
  state_key: '',
  content: {
    algorithm: 'm.megolm.v1.aes-sha2',
  },
};

export const buildRestrictedJoinRuleState = (roomId: string): ICreateRoomStateEvent => ({
  type: EventType.RoomJoinRules,
  state_key: '',
  content: {
    join_rule: JoinRule.Restricted,
    allow: [{ type: RestrictedAllowType.RoomMembership, room_id: roomId }],
  },
});

const privateRoomStates = [
  {
    type: CustomMeedioMatrixEvents.IS_PRIVATE_ROOM,
    state_key: '',
    content: { isPrivate: true },
  },
];

type BuildInitialStatesParams = {
  matrixClient: MatrixClient;
  workspaceId: string | null;
  restrictedToRoomId?: string | null;
  isRoomE2EEEnabled: boolean;
  isWaitingListEnabled: boolean;
  isPrivateRoom: boolean;
};

export const buildMeetingInitialStates = ({
  matrixClient,
  workspaceId,
  restrictedToRoomId,
  isRoomE2EEEnabled,
  isWaitingListEnabled,
  isPrivateRoom,
}: BuildInitialStatesParams) => {
  const encryptionStates = isRoomE2EEEnabled ? [encryptionState] : [];
  const privateRoomInitialStates: ICreateRoomStateEvent[] = isPrivateRoom ? privateRoomStates : [];
  // #8033 — waiting-list-only rooms get a persistent WAS_WAITING_LIST_ROOM marker so the inline
  // edit toggles stay visible after the waiting list is turned off. Private rooms already have
  // IS_PRIVATE_ROOM as their persistent marker, so the marker is omitted on that path.
  const waitingListInitialStates: ICreateRoomStateEvent[] =
    isWaitingListEnabled && !isPrivateRoom
      ? [
          waitingListState,
          { type: CustomMeedioMatrixEvents.WAS_WAITING_LIST_ROOM, state_key: '', content: { wasEnabled: true } },
        ]
      : isWaitingListEnabled
      ? [waitingListState]
      : [];
  const workspaceRoomStates = workspaceId
    ? [buildCanonicalParentEvent(workspaceId, getViaServers(workspaceId, matrixClient))]
    : [];

  const workspaceRestrictedJoinState: ICreateRoomStateEvent[] =
    restrictedToRoomId && !isPrivateRoom && !isWaitingListEnabled
      ? [buildRestrictedJoinRuleState(restrictedToRoomId)]
      : [];

  return [
    ...encryptionStates,
    ...privateRoomInitialStates,
    ...waitingListInitialStates,
    ...workspaceRoomStates,
    ...workspaceRestrictedJoinState,
  ];
};

/** Workspace standalone meeting metadata events — meeting-scoped PLs (not workspace create PLs). */
export const V2_WORKSPACE_MEETING_METADATA_EVENT_THRESHOLDS = {
  [EventType.RoomName]: workspacesConfig[PermissionId.MeetingEditDetails].powerLevel,
  [EventType.RoomCanonicalAlias]: workspacesConfig[PermissionId.MeetingEditDetails].powerLevel,
  [CustomMeedioMatrixEvents.IS_PRIVATE_ROOM]: workspacesConfig[PermissionId.MeetingEditDetails].powerLevel,
  [CustomMeedioMatrixEvents.IS_SCHEDULED_CALL_STATE_EVENT]:
    workspacesConfig[PermissionId.MeetingEditDetails].powerLevel,
} as const;

/** Applied only for workspace meetings via `useCreateMeeting` (not chat-embedded call spaces). */
export const getWorkspaceMeetingPowerLevelOverrides = () => ({
  events: { ...V2_WORKSPACE_MEETING_METADATA_EVENT_THRESHOLDS },
});
